import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../navigation/RootNavigator';
import { useTranslation } from 'react-i18next';
import Icon from 'react-native-vector-icons/Ionicons';
import { Picker } from '@react-native-picker/picker';
import { api } from '../services/api';
import logger from '../utils/logger';
import { useTheme } from '../contexts/ThemeContext';

interface AlarmRule {
  highThreshold: string;
  lowThreshold: string;
  hysteresis: string;
  debounceSeconds: string;
  cooldownMinutes: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  notificationsEnabled: boolean;
}

type DeviceConfigNavigationProp = StackNavigationProp<RootStackParamList, 'DeviceConfig'>;
type DeviceConfigRouteProp = RouteProp<RootStackParamList, 'DeviceConfig'>;

export default function DeviceConfigScreen() {
  const route = useRoute<DeviceConfigRouteProp>();
  const navigation = useNavigation<DeviceConfigNavigationProp>();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const isRTL = i18n.language === 'ar';
  const styles = createStyles(theme);

  const { serialNumber, sensor, detection, mode } = route.params as any;

  const [deviceName, setDeviceName] = useState('');
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [zones, setZones] = useState<any[]>([]);

  // Alarm Rules
  const [alarmRule, setAlarmRule] = useState<AlarmRule>({
    highThreshold: '25',
    lowThreshold: '2',
    hysteresis: '0.5',
    debounceSeconds: '60',
    cooldownMinutes: '5',
    severity: 'high',
    notificationsEnabled: true,
  });

  const [loading, setLoading] = useState(false);
  const [loadingZones, setLoadingZones] = useState(true);

  useEffect(() => {
    // Set suggested name
    if (sensor?.suggestedName) {
      setDeviceName(sensor.suggestedName);
    } else if (detection?.sensor?.suggestedName) {
      setDeviceName(detection.sensor.suggestedName);
    }

    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await api.get('/zones');
      if (response.data.success) {
        setZones(response.data.data);
        logger.log('✅ Loaded zones:', response.data.data.length);
      }
    } catch (err) {
      logger.error('Failed to load zones:', err);
    } finally {
      setLoadingZones(false);
    }
  };

  const validateInputs = () => {
    if (!deviceName.trim()) {
      Alert.alert(t('error'), t('device_name_required'));
      return false;
    }

    const high = parseFloat(alarmRule.highThreshold);
    const low = parseFloat(alarmRule.lowThreshold);
    const hyst = parseFloat(alarmRule.hysteresis);

    if (isNaN(high) || isNaN(low) || isNaN(hyst)) {
      Alert.alert(t('error'), t('invalid_threshold_values'));
      return false;
    }

    if (high <= low) {
      Alert.alert(t('error'), t('high_threshold_must_be_greater'));
      return false;
    }

    if (hyst <= 0) {
      Alert.alert(t('error'), t('hysteresis_must_be_positive'));
      return false;
    }

    return true;
  };

  const handleCreateDevice = async () => {
    if (!validateInputs()) return;

    try {
      setLoading(true);
      logger.log('🚀 Creating device from serial:', serialNumber);

      const payload = {
        serialNumber,
        deviceName: deviceName.trim(),
        zoneId: selectedZone || undefined,
        selectedMetrics: ['temperature', 'humidity'], // Always required
        alarmRule: {
          highThreshold: parseFloat(alarmRule.highThreshold),
          lowThreshold: parseFloat(alarmRule.lowThreshold),
          hysteresis: parseFloat(alarmRule.hysteresis),
          debounceSeconds: parseInt(alarmRule.debounceSeconds),
          cooldownMinutes: parseInt(alarmRule.cooldownMinutes),
          severity: alarmRule.severity,
          notificationsEnabled: alarmRule.notificationsEnabled,
        },
      };

      const response = await api.post('/devices/create-from-serial', payload);

      if (response.data.success) {
        logger.log('✅ Device created successfully');

        navigation.navigate('ProvisionSuccess', {
          device: response.data.data.device,
          mqttPassword: response.data.data.mqttPassword,
        });
      } else {
        throw new Error(response.data.error?.message || t('failed_to_create_device'));
      }
    } catch (err: any) {
      logger.error('Failed to create device:', err);
      Alert.alert(t('error'), err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name={isRTL ? 'chevron-forward' : 'chevron-back'} size={28} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('device_configuration')}</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Device Info Card */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('device_information')}</Text>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('device_name')} *</Text>
            <TextInput
              style={styles.input}
              value={deviceName}
              onChangeText={setDeviceName}
              placeholder={t('enter_device_name')}
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('zone')}</Text>
            {loadingZones ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <View style={styles.pickerContainer}>
                <Picker
                  selectedValue={selectedZone}
                  onValueChange={setSelectedZone}
                  style={styles.picker}
                >
                  <Picker.Item label={t('select_zone')} value="" />
                  {zones.map((zone) => (
                    <Picker.Item key={zone.id} label={zone.name} value={zone.id} />
                  ))}
                </Picker>
              </View>
            )}
          </View>
        </View>

        {/* Alarm Rules Card */}
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Icon name="notifications" size={24} color={theme.colors.warning} />
            <Text style={styles.cardTitle}>{t('alarm_rules')}</Text>
          </View>
          <Text style={styles.cardSubtitle}>{t('set_temperature_thresholds')}</Text>

          <View style={styles.thresholdRow}>
            <View style={styles.thresholdInput}>
              <Text style={styles.label}>{t('high_threshold')} (°C) *</Text>
              <TextInput
                style={styles.input}
                value={alarmRule.highThreshold}
                onChangeText={(val) => setAlarmRule({ ...alarmRule, highThreshold: val })}
                keyboardType="numeric"
                placeholder="25"
              />
            </View>

            <View style={styles.thresholdInput}>
              <Text style={styles.label}>{t('low_threshold')} (°C) *</Text>
              <TextInput
                style={styles.input}
                value={alarmRule.lowThreshold}
                onChangeText={(val) => setAlarmRule({ ...alarmRule, lowThreshold: val })}
                keyboardType="numeric"
                placeholder="2"
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('hysteresis')} (°C)</Text>
            <TextInput
              style={styles.input}
              value={alarmRule.hysteresis}
              onChangeText={(val) => setAlarmRule({ ...alarmRule, hysteresis: val })}
              keyboardType="numeric"
              placeholder="0.5"
            />
            <Text style={styles.helpText}>{t('hysteresis_help')}</Text>
          </View>

          <View style={styles.thresholdRow}>
            <View style={styles.thresholdInput}>
              <Text style={styles.label}>
                {t('debounce')} ({t('seconds')})
              </Text>
              <TextInput
                style={styles.input}
                value={alarmRule.debounceSeconds}
                onChangeText={(val) => setAlarmRule({ ...alarmRule, debounceSeconds: val })}
                keyboardType="numeric"
                placeholder="60"
              />
            </View>

            <View style={styles.thresholdInput}>
              <Text style={styles.label}>
                {t('cooldown')} ({t('minutes')})
              </Text>
              <TextInput
                style={styles.input}
                value={alarmRule.cooldownMinutes}
                onChangeText={(val) => setAlarmRule({ ...alarmRule, cooldownMinutes: val })}
                keyboardType="numeric"
                placeholder="5"
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>{t('severity')}</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={alarmRule.severity}
                onValueChange={(val) => setAlarmRule({ ...alarmRule, severity: val })}
                style={styles.picker}
              >
                <Picker.Item label={t('low')} value="low" />
                <Picker.Item label={t('medium')} value="medium" />
                <Picker.Item label={t('high')} value="high" />
                <Picker.Item label={t('critical')} value="critical" />
              </Picker>
            </View>
          </View>

          <TouchableOpacity
            style={styles.notificationToggle}
            onPress={() =>
              setAlarmRule({
                ...alarmRule,
                notificationsEnabled: !alarmRule.notificationsEnabled,
              })
            }
          >
            <View style={styles.toggleLeft}>
              <Icon
                name={alarmRule.notificationsEnabled ? 'notifications' : 'notifications-off'}
                size={24}
                color={alarmRule.notificationsEnabled ? theme.colors.success : '#999'}
              />
              <Text style={styles.toggleText}>{t('enable_notifications')}</Text>
            </View>
            <View
              style={[
                styles.toggleSwitch,
                alarmRule.notificationsEnabled && styles.toggleSwitchActive,
              ]}
            >
              <View
                style={[
                  styles.toggleThumb,
                  alarmRule.notificationsEnabled && styles.toggleThumbActive,
                ]}
              />
            </View>
          </TouchableOpacity>
        </View>

        {/* Summary Card */}
        <View style={styles.summaryCard}>
          <Text style={styles.summaryTitle}>{t('summary')}</Text>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('device_name')}:</Text>
            <Text style={styles.summaryValue}>{deviceName || '-'}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('zone')}:</Text>
            <Text style={styles.summaryValue}>
              {zones.find((z) => z.id === selectedZone)?.name || t('not_selected')}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>{t('alarm_range')}:</Text>
            <Text style={styles.summaryValue}>
              {alarmRule.lowThreshold}°C - {alarmRule.highThreshold}°C
            </Text>
          </View>
        </View>

        <View style={{ height: 100 }} />
      </ScrollView>

      {/* Create Button */}
      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.createButton, loading && styles.createButtonDisabled]}
          onPress={handleCreateDevice}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Text style={styles.createButtonText}>{t('create_device')}</Text>
              <Icon name="checkmark-circle" size={24} color="#fff" />
            </>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: theme.colors.card,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    backButton: {
      padding: 8,
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.text,
    },
    content: {
      flex: 1,
      paddingHorizontal: 16,
      paddingTop: 16,
    },
    card: {
      backgroundColor: theme.colors.card,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 8,
    },
    cardTitle: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.text,
      marginLeft: 8,
    },
    cardSubtitle: {
      fontSize: 14,
      color: theme.colors.textSecondary,
      marginBottom: 16,
    },
    inputGroup: {
      marginBottom: 16,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
    },
    input: {
      backgroundColor: theme.colors.background,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 12,
      fontSize: 16,
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    pickerContainer: {
      backgroundColor: theme.colors.background,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      overflow: 'hidden',
    },
    picker: {
      height: 50,
    },
    thresholdRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    thresholdInput: {
      flex: 1,
      marginRight: 8,
    },
    helpText: {
      fontSize: 12,
      color: theme.colors.textSecondary,
      marginTop: 4,
    },
    notificationToggle: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      paddingHorizontal: 12,
      backgroundColor: theme.colors.background,
      borderRadius: 8,
      marginTop: 8,
    },
    toggleLeft: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    toggleText: {
      fontSize: 16,
      color: theme.colors.text,
      marginLeft: 12,
    },
    toggleSwitch: {
      width: 48,
      height: 28,
      borderRadius: 14,
      backgroundColor: theme.colors.border,
      padding: 2,
    },
    toggleSwitchActive: {
      backgroundColor: theme.colors.success,
    },
    toggleThumb: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: '#fff',
    },
    toggleThumbActive: {
      alignSelf: 'flex-end',
    },
    summaryCard: {
      backgroundColor: theme.colors.info + '20',
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
    },
    summaryTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.info,
      marginBottom: 12,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 6,
    },
    summaryLabel: {
      fontSize: 14,
      color: theme.colors.textSecondary,
    },
    summaryValue: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
    },
    footer: {
      padding: 16,
      backgroundColor: theme.colors.card,
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    createButton: {
      backgroundColor: theme.colors.success,
      borderRadius: 12,
      paddingVertical: 16,
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    createButtonDisabled: {
      opacity: 0.6,
    },
    createButtonText: {
      fontSize: 18,
      fontWeight: '600',
      color: '#fff',
      marginRight: 8,
    },
  });
